import {useCallback} from 'react';

import {AccountFieldsPrefix} from '@/constants/prv-comp';
import {SECTION_I, SECTION_J_PLUS, SECTION_O, SECTION_R} from '@/constants/sections';
import {DocumentData} from '@/engine';
import {logger} from '@/initializers/logger';
import {DetailedInvestorAccount} from '@/types/client-info';
import {detectRemovedInvestments, detectRemovedPacs} from '@/utils/client-info/account-refresh';
import {useClearArrayFields} from './use-clear-array-fields';

const LOG_PREFIX = '[useDetectAndClearRemovedInvestments]';

const SECTIONS_TO_CLEAR: Record<AccountFieldsPrefix, string[]> = {
  COMP: [SECTION_R, SECTION_O, SECTION_I, SECTION_J_PLUS],
  PRV_COMP: [SECTION_I, SECTION_J_PLUS],
};

const PAC_SECTIONS_TO_CLEAR: Record<AccountFieldsPrefix, string[]> = {
  COMP: [SECTION_J_PLUS],
  PRV_COMP: [],
};

type DetectAndClearRemovedInvestmentsParams = {
  oldData: DocumentData['data'];
  freshAccount: DetailedInvestorAccount;
  prefix: AccountFieldsPrefix;
};

export const useDetectAndClearRemovedInvestments = (): ((
  params: DetectAndClearRemovedInvestmentsParams,
) => boolean) => {
  const clearArrayFields = useClearArrayFields();

  return useCallback(
    ({oldData, freshAccount, prefix}: DetectAndClearRemovedInvestmentsParams): boolean => {
      const removedFundCodes = detectRemovedInvestments(oldData, freshAccount, prefix);
      const removedPacFundCodes = prefix === 'COMP' ? detectRemovedPacs(oldData, freshAccount, prefix) : [];

      const sectionsToClear = new Set<string>([
        ...(removedFundCodes.length > 0 ? SECTIONS_TO_CLEAR[prefix] : []),
        ...(removedPacFundCodes.length > 0 ? PAC_SECTIONS_TO_CLEAR[prefix] : []),
      ]);

      if (sectionsToClear.size === 0) return false;

      const clearedSectionArrays = Array.from(sectionsToClear);

      logger.info(`${LOG_PREFIX} Clearing sections because of removed investments or PACs`, {
        prefix,
        removedFundCodes,
        removedPacFundCodes,
        clearedSectionArrays,
      });

      clearArrayFields(clearedSectionArrays);
      return true;
    },
    [clearArrayFields],
  );
};
